import React, { useState, useEffect } from "react";
import { SnapTradeReact } from 'snaptrade-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "../css/AccountManager.css";
import loadingAnimation from "../assets/Loading.json";
import Lottie from "lottie-react";
import { useNavigate } from "react-router-dom";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { getAccounts, connectAccount, setupAccount, toggleAccountActive, cleanupAccounts } from '../api/accounts';

function Accounts({ username }) {
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [loginLink, setLoginLink] = useState('');
    const [expandedAccount, setExpandedAccount] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchAccounts();
    }, []);

    const fetchAccounts = async () => {
        try {
            const data = await getAccounts();
            setAccounts(data);
        } catch (err) {
            setError("Failed to load accounts.");
        }
    };

    const toggleExpand = (accountId) => {
        setExpandedAccount(expandedAccount === accountId ? null : accountId);
    };


    const handleConnect = async () => {
        try {
            const data = await connectAccount();
            setLoginLink(data.redirectUrl);
            setIsOpen(true);
        } catch (err) {
            console.error("Error connecting account:", err);
        }
    };

    const handleSuccess = async (brokerageAuthorizationId) => {
        setIsOpen(false);
        setLoading(true);
        try {
            console.log("Authorization ID:", brokerageAuthorizationId);
            const data = await setupAccount(brokerageAuthorizationId);
            if (data === "Account already exists") {
                localStorage.setItem('snaptrade_connection_status', JSON.stringify({ type: 'warning', message: 'Account already exists.' }));
                window.dispatchEvent(new CustomEvent('snaptradeConnectionComplete', { detail: { type: 'warning', message: 'Account already exists.' } }));
            } else if (data === "Username/brokerage auth is empty or null") {
                localStorage.setItem('snaptrade_connection_status', JSON.stringify({ type: 'error', message: 'Invalid user. Please sign-in again.' }));
                window.dispatchEvent(new CustomEvent('snaptradeConnectionComplete', { detail: { type: 'error', message: 'Invalid user. Please sign-in again.' } }));
            } else {
                localStorage.setItem('snaptrade_connection_status', JSON.stringify({ type: 'success', message: 'Connection successful! We will process the data on the backend side.' }));
                window.dispatchEvent(new CustomEvent('snaptradeConnectionComplete', { detail: { type: 'success', message: 'Connection successful! We will process the data on the backend side.' } }));
            }
            fetchAccounts();
        } catch (err) {
            console.error("Error fetching accounts:", err);
            localStorage.setItem('snaptrade_connection_status', JSON.stringify({ type: 'error', message: 'Connection failed. Please try to reconnect the account. We will process the data on the backend side.' }));
            window.dispatchEvent(new CustomEvent('snaptradeConnectionComplete', { detail: { type: 'error', message: 'Connection failed. Please try to reconnect the account. We will process the data on the backend side.' } }));
            setError("Failed to load accounts.");
        } finally {
            setLoading(false);
        }
        // Proceed with activating the account using the authorizationId
    };
    
    const handleError = async (error) => {
        console.log('Error:', error);

        try {
            await cleanupAccounts(JSON.stringify(error));

            localStorage.setItem('snaptrade_connection_status', JSON.stringify({ type: 'error', message: 'Connection failed. Please try again. We will process the data on the backend side.' }));
            window.dispatchEvent(new CustomEvent('snaptradeConnectionComplete', { detail: { type: 'error', message: 'Connection failed. Please try again. We will process the data on the backend side.' } }));
            setIsOpen(false);
        } catch (e) {
            console.error("Error cleaning up account:", e);
            localStorage.setItem('snaptrade_connection_status', JSON.stringify({ type: 'error', message: 'Connection failed. Please try again. We will process the data on the backend side.' }));
            window.dispatchEvent(new CustomEvent('snaptradeConnectionComplete', { detail: { type: 'error', message: 'Connection failed. Please try again. We will process the data on the backend side.' } }));
        }
    };
    
    const handleExit = async () => {
        setIsOpen(false);
        console.log('Error:', error);

        try {
            await cleanupAccounts(JSON.stringify(error));

            alert("Connection error. Please try again.");
            setIsOpen(false);
        } catch (e) {
            console.error("Error cleaning up account:", e);
        }
    };

    const toggleAccountStatus = async (accountId, currentStatus) => {
        try {
            await toggleAccountActive(accountId);
            setAccounts((prevAccounts) => prevAccounts.map((account) => account.accountId === accountId ? {
                ...account,
                enabled: !currentStatus
            } : account));
            setTimeout(() => {
                window.location.reload();
            }, 2000);
        } catch (err) {
            console.error("Error toggling account status:", err);
            setError("Failed to update account status.");
        }
    };

    const sleep = (ms) => {
        return new Promise((resolve) => setTimeout(resolve, ms));
    };
    if (loading) {
        return (<div
                style={{
                    display: "flex", justifyContent: "center", alignItems: "center"
                }}
            >
                <Lottie
                    animationData={loadingAnimation}
                    style={{height: "150px", width: "150px"}} // Adjust loader size
                />
            </div>);
    }
    if (error) return <div className="error">{error}</div>;

    return (<div className="account-manager">
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            <h1>Account Manager</h1>
            <div>
                <div className="tooltip-container">
                    <button className="add-account-button button-spacing" onClick={handleConnect}>
                        Connect Account
                    </button>
                    <span className="tooltip">
                    <strong>Your Data is Safe:</strong> ProfiTree connects securely via SnapTrade using
                advanced encryption and read-only access.
                    </span>
                </div>
                <button className="add-account-button button-spacing" onClick={() => navigate("/tax-insights")}>
                    Finish Connection
                </button>
                {isOpen && (<SnapTradeReact
                        loginLink={loginLink}
                        isOpen={isOpen}
                        onSuccess={handleSuccess}
                        onError={handleError}
                        onExit={handleExit}
                        close={() => setIsOpen(false)}
                    />)}
            </div>

            {/* Table for Larger Screens */}
            <div className="responsive-container">
                <table className="responsive-table">
                    <thead>
                    <tr>
                        <th>Account Name</th>
                        <th>Created Date</th>
                        <th>Status</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {accounts.map((account) => (
                        <tr key={account.accountId}>
                            <td>{account.accountName}</td>
                            <td>{account.createdDate}</td>
                            <td>{account.enabled ? "Enabled" : "Disabled"}</td>
                            <td>
                                {account.enabled ? (<button
                                    className="status-disable"
                                    onClick={() => toggleAccountStatus(account.accountId, false)}
                                >
                                    Disable
                                </button>) : (<button
                                    className="action-button-enable"
                                    onClick={() => toggleAccountStatus(account.accountId, true)}
                                >
                                    Enable
                                </button>)}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>

                {/* Card View for Mobile Screens */}
                <div className="responsive-cards">
                    {accounts.map((account) => (
                        <div key={account.accountId} className="account-row">
                            <div className="account-summary" onClick={() => toggleExpand(account.accountId)}>
                                <div className="account-name">
                                    {account.accountName}
                                    {expandedAccount === account.accountId ? <FaChevronUp className="chevron-icon" /> : <FaChevronDown className="chevron-icon" />}
                                </div>
                            </div>
                            {expandedAccount === account.accountId && (
                                <div className="account-details">
                                    <p><strong>Created:</strong> {account.createdDate}</p>
                                    <p><strong>Status:</strong> {account.enabled ? "Enabled" : "Disabled"}</p>
                                    <button
                                        className={`status-${account.enabled ? "disable" : "enable"}`}
                                        onClick={() => toggleAccountStatus(account.accountId, account.enabled)}
                                    >
                                        {account.enabled ? "Disable" : "Enable"}
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default Accounts;
